import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/** What the server sends with an IncomingEmailReceived event. A summary, never the message. */
export interface IncomingEmailArrival {
  /** IncomingEmail.Id of the row that was just written. */
  id: number;
  /** The organisation the mailbox belongs to. */
  organizationId: string;
  from?: string;
  fromEmail?: string;
  subject?: string;
  toEmail?: string;
  submissionMethod?: number;
  attachmentCount?: number;
  /** ISO-8601, UTC. */
  receivedAt?: string;
  /** Which ingest path it came from: 'gmail', 'outlook', or the forwarded submission method. */
  source?: string;
}

/**
 * Carries "an email just arrived" from the notifications hub to whoever is showing emails.
 *
 * NotificationsHubService is a root singleton started at sign-in; the Emails page mounts and
 * unmounts. Republishing through here keeps the hub service free of any knowledge of who is
 * listening, which is the same shape WorkflowRunPanelService already uses for run updates.
 *
 * Deliberately not a ReplaySubject: a page that mounts later fetches the list anyway, so a
 * backlog of arrivals it is already showing would only make it fetch again.
 */
@Injectable({ providedIn: 'root' })
export class IncomingEmailService {
  private readonly _arrived = new Subject<IncomingEmailArrival>();

  /**
   * Every arrival, whichever organisation it belongs to.
   *
   * Not filtered here: a person can belong to several organisations on one connection, and which
   * one they are looking at can change without this service knowing. Subscribers compare
   * `organizationId` against whatever they are showing at the time.
   */
  readonly arrived$ = this._arrived.asObservable();

  /** Called by NotificationsHubService on an IncomingEmailReceived event. */
  notifyArrived(arrival: IncomingEmailArrival): void {
    if (arrival) { this._arrived.next(arrival); }
  }
}
